import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Divider,
  Button,
} from '@mui/material';
import Tooltip from '../LibraryTooltip';
import {
  ChevronRightOutlined as ChevronRightIcon,
  ChevronLeftOutlined as ChevronLeftIcon,
  AddOutlined as AddIcon,
  InfoOutlined as InfoOutlinedIcon,
  MenuBookOutlined as KnowledgeIcon,
} from '@mui/icons-material';
import ContextAddMenu from '../context-display/ContextAddMenu';
import KnowledgeShelfList from './KnowledgeShelfList';
import useLibrarySessionState from '../../library/useLibrarySessionState';
import ExecutePromptsSection from './ExecutePromptsSection';
import {
  expandedPromptShelfContentSx,
  expandedPromptShelfSectionHeaderRowSx,
  expandedPromptShellSectionTitleSx,
  promptDetailSectionHeaderSx,
} from './expandedPromptShellPaperSx';
import { colors, primarySectionHeaderSx } from '../../../designTokens';
import { PRODUCT_TERMS } from '../../../constants/productTerminology';

/** Content width — readable column before collapse rail. */
const SHELF_CONTENT_WIDTH = 220;
/** Execute mode — wider shelf for capability + knowledge navigation. */
const EXECUTE_SHELF_CONTENT_WIDTH = 220;

const KNOWLEDGE_WHAT_COUNTS_ITEMS = [
  'Reference documents',
  'Examples',
  'Style guides',
  'Integration guides',
  'Templates',
  'Background information',
];

const KNOWLEDGE_INFO_TOOLTIP = (
  <Box component="ul" sx={{ m: 0, pl: 2, py: 0.25 }}>
    {KNOWLEDGE_WHAT_COUNTS_ITEMS.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </Box>
);

const SHELF_QUIET_BORDER = 'rgba(15, 23, 42, 0.08)';

function ShelfSectionHeader({
  title,
  onAdd,
  addTooltip = 'Add',
  infoTooltip,
  titleSx,
  sx,
  headerAction = null,
}) {
  return (
    <Box sx={{ ...expandedPromptShelfSectionHeaderRowSx, ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, flex: 1, minWidth: 0 }}>
        <Typography component="h3" sx={{ ...(titleSx || expandedPromptShellSectionTitleSx) }}>
          {title}
        </Typography>
        {infoTooltip && (
          <Tooltip title={infoTooltip} arrow placement="top">
            <IconButton
              size="small"
              aria-label="What counts as knowledge?"
              sx={{ p: 0.2, color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {headerAction}
      {!headerAction && typeof onAdd === 'function' && (
        <Tooltip title={addTooltip}>
          <IconButton size="small" aria-label={addTooltip} onClick={onAdd} sx={{ p: 0.35, color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'rgba(29, 78, 216, 0.06)' } }}>
            <AddIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

function CapabilitySection({
  isExecuteView,
  prompts,
  onPromptOpenFullScreen,
  onPromptExecute,
  onPromptRemove,
  onOpenVariables,
  isPromptExecuting,
  activePromptId,
  onAddPrompt,
  variableValues,
  singleCapability,
}) {
  const sectionTitle = singleCapability ? 'Active capability' : isExecuteView && prompts.length === 1 ? 'Capability' : PRODUCT_TERMS.capability.pluralTitleCase;
  return (
    <>
      <ShelfSectionHeader
        title={sectionTitle}
        titleSx={isExecuteView
          ? {
            ...promptDetailSectionHeaderSx,
            fontSize: '0.875rem',
            fontWeight: 700,
            letterSpacing: 0,
          }
          : undefined}
        addTooltip={isExecuteView ? 'Add' : `Add ${PRODUCT_TERMS.capability.singular}`}
        onAdd={onAddPrompt}
        headerAction={singleCapability && prompts.length > 0 ? <Button size="small" onClick={onAddPrompt} sx={{ minWidth: 0, fontSize: '0.75rem' }}>Replace</Button> : null}
        sx={{ mt: 0 }}
      />
      <ExecutePromptsSection
        prompts={prompts}
        onOpenFullScreen={onPromptOpenFullScreen}
        onExecute={onPromptExecute}
        onRemove={onPromptRemove}
        onOpenVariables={onOpenVariables}
        variableValues={variableValues}
        isExecuting={isPromptExecuting}
        activePromptId={activePromptId}
      />
    </>
  );
}

function KnowledgeSection({
  contexts,
  hasContexts,
  contextCategories,
  expandedCategories,
  setExpandedCategories,
  includedContexts,
  onIncludeChange,
  onPreview,
  onRequestAccess,
  onAddTemporaryContext,
  showTemporaryMenu,
  addContextMenuAnchor,
  setAddContextMenuAnchor,
  onAttachExistingContext,
  isExecuteView = false,
}) {
  const includedCount = contexts.filter((context) => context.hasAccess !== false && includedContexts[context.id] !== false).length;
  const openAddMenu = (e) => {
    if (showTemporaryMenu) {
      setAddContextMenuAnchor(e.currentTarget);
    } else {
      onAddTemporaryContext();
    }
  };

  return (
    <>
      <ShelfSectionHeader
        title="Knowledge"
        titleSx={primarySectionHeaderSx}
        addTooltip="Add knowledge"
        infoTooltip={<Box>{KNOWLEDGE_INFO_TOOLTIP}<Typography variant="caption">{isExecuteView ? 'Checked items are used in this session.' : 'Checked items are included when you use this capability.'} Unchecking does not delete saved knowledge.</Typography></Box>}
        onAdd={hasContexts && typeof onAddTemporaryContext === 'function' ? openAddMenu : undefined}
      />
      {hasContexts && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        {includedCount === contexts.length ? `${includedCount} included` : `${includedCount} of ${contexts.length} included`}
      </Typography>}
      {!hasContexts && typeof onAddTemporaryContext === 'function' && (
        <Box sx={{ px: 0.25, pb: 1.25 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon sx={{ fontSize: 15 }} />}
            onClick={openAddMenu}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              py: 0.375,
              minHeight: 28,
              borderColor: SHELF_QUIET_BORDER,
              color: 'text.secondary',
              '&:hover': {
                borderColor: 'rgba(15, 23, 42, 0.16)',
                bgcolor: 'rgba(15, 23, 42, 0.03)',
              },
            }}
          >
            Add knowledge
          </Button>
        </Box>
      )}
      {hasContexts && (
        <KnowledgeShelfList
          contexts={contexts}
          contextCategories={contextCategories}
          expandedCategories={expandedCategories}
          setExpandedCategories={setExpandedCategories}
          includedContexts={includedContexts}
          onIncludeChange={onIncludeChange}
          onPreview={onPreview}
          onRequestAccess={onRequestAccess}
        />
      )}
      {typeof onAddTemporaryContext === 'function' && (
        <ContextAddMenu
          anchorEl={addContextMenuAnchor}
          open={Boolean(addContextMenuAnchor)}
          onClose={() => setAddContextMenuAnchor(null)}
          onCreateTemporary={onAddTemporaryContext}
          onAttachExisting={onAttachExistingContext}
        />
      )}
    </>
  );
}

/**
 * Collapsible side shelf that shows contexts grouped by category and, in execute mode, a capability section.
 * Execute view: Current Capability first, then Knowledge.
 */
export default function PromptContextShelf({
  contexts = [],
  contextCategories = {},
  includedContexts = {},
  onIncludeChange,
  onPreview,
  onRequestAccess,
  onAddTemporaryContext,
  onAttachExistingContext,
  prompts = [],
  onAddPrompt,
  onPromptOpenFullScreen,
  onPromptExecute,
  onPromptRemove,
  onOpenVariables,
  isPromptExecuting,
  activePromptId,
  isExecuteView = false,
  showCollapseRail = true,
  contentWidth,
  useExecuteChrome = false,
  shelfTopAction = null,
  onCollapseRailClick = null,
  onDropFiles = null,
  mobileOverlay = false,
  onMobileOverlayClose = null,
  layoutMode = 'detail',
  variableValues = {},
  singleCapability = false,
  sessionScope = 'capability-workspace',
  defaultExpanded,
}) {
  const hasContexts = contexts && contexts.length > 0;
  const hasPrompts = prompts && prompts.length > 0;
  const showContextSection = hasContexts || typeof onAddTemporaryContext === 'function';
  const showPromptsSection = hasPrompts || typeof onAddPrompt === 'function';
  const showTemporaryMenu = typeof onAddTemporaryContext === 'function' && typeof onAttachExistingContext === 'function';

  const [expanded, setExpanded] = useLibrarySessionState(sessionScope, `shelf:${layoutMode}`, () => defaultExpanded ?? (layoutMode !== 'creator' && (isExecuteView || !!hasContexts || !!hasPrompts)));
  const [expandedCategories, setExpandedCategories] = useLibrarySessionState(sessionScope, 'knowledge-types', {});
  const [addContextMenuAnchor, setAddContextMenuAnchor] = useState(null);
  const [isShelfDragActive, setIsShelfDragActive] = useState(false);

  if (!showContextSection && !hasPrompts) return null;

  const isExpanded = mobileOverlay || !showCollapseRail || expanded;
  const hasExecuteChrome = isExecuteView || useExecuteChrome;
  const desktopShelfContentWidth = contentWidth || (hasExecuteChrome ? EXECUTE_SHELF_CONTENT_WIDTH : SHELF_CONTENT_WIDTH);
  const collapseRailWidth = showCollapseRail ? (hasExecuteChrome ? 34 : 40) : 0;
  const shelfContentWidth = mobileOverlay
    ? `calc(75vw - ${collapseRailWidth}px)`
    : desktopShelfContentWidth;
  const width = isExpanded
    ? (mobileOverlay ? `calc(${shelfContentWidth} + ${collapseRailWidth}px)` : shelfContentWidth + collapseRailWidth)
    : 40;
  const hasDraggedFiles = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');
  const handleShelfDragEnter = (event) => {
    if (!onDropFiles || !hasDraggedFiles(event)) return;
    event.preventDefault();
    setIsShelfDragActive(true);
  };
  const handleShelfDragOver = (event) => {
    if (!onDropFiles || !hasDraggedFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setIsShelfDragActive(true);
  };
  const handleShelfDragLeave = (event) => {
    if (!onDropFiles || !hasDraggedFiles(event)) return;
    const currentTarget = event.currentTarget;
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && currentTarget?.contains?.(relatedTarget)) return;
    setIsShelfDragActive(false);
  };
  const handleShelfDrop = (event) => {
    if (!onDropFiles) return;
    const files = Array.from(event.dataTransfer?.files || []);
    if (!files.length) return;
    event.preventDefault();
    setIsShelfDragActive(false);
    onDropFiles(files);
  };

  const capabilityBlock = showPromptsSection ? (
    <CapabilitySection
      isExecuteView={isExecuteView}
      prompts={prompts}
      onPromptOpenFullScreen={onPromptOpenFullScreen}
      onPromptExecute={onPromptExecute}
      onPromptRemove={onPromptRemove}
      onOpenVariables={onOpenVariables}
      isPromptExecuting={isPromptExecuting}
      activePromptId={activePromptId}
      onAddPrompt={onAddPrompt}
      variableValues={variableValues}
      singleCapability={singleCapability}
    />
  ) : null;

  const knowledgeBlock = showContextSection ? (
    <KnowledgeSection
      contexts={contexts}
      hasContexts={hasContexts}
      contextCategories={contextCategories}
      expandedCategories={expandedCategories}
      setExpandedCategories={setExpandedCategories}
      includedContexts={includedContexts}
      onIncludeChange={onIncludeChange}
      onPreview={onPreview}
      onRequestAccess={onRequestAccess}
      onAddTemporaryContext={onAddTemporaryContext}
      showTemporaryMenu={showTemporaryMenu}
      addContextMenuAnchor={addContextMenuAnchor}
      setAddContextMenuAnchor={setAddContextMenuAnchor}
      onAttachExistingContext={onAttachExistingContext}
      isExecuteView={isExecuteView}
    />
  ) : null;

  const shelf = (
    <Box
      onDragEnter={handleShelfDragEnter}
      onDragOver={handleShelfDragOver}
      onDragLeave={handleShelfDragLeave}
      onDrop={handleShelfDrop}
      sx={{
        width,
        minWidth: width,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'row',
        borderRight: '1px solid rgba(15, 23, 42, 0.08)',
        bgcolor: isShelfDragActive ? 'action.hover' : 'background.paper',
        transition: 'width 0.2s ease',
        overflow: 'hidden',
        alignSelf: 'stretch',
      }}
    >
      <Collapse in={isExpanded} orientation="horizontal" sx={{ flex: 1 }}>
        <Box
          sx={{
            width: shelfContentWidth,
            minWidth: shelfContentWidth,
            maxHeight: '100%',
            overflow: 'auto',
            ...expandedPromptShelfContentSx,
            ...(hasExecuteChrome ? { pl: 2.125, pr: 1.375, py: 1.75 } : {}),
            bgcolor: 'background.paper',
          }}
        >
          {isExecuteView ? (
            <>
              {shelfTopAction && <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>{shelfTopAction}</Box>}
              {capabilityBlock}
              {showPromptsSection && showContextSection && <Divider sx={{ my: 1.75, borderColor: SHELF_QUIET_BORDER }} />}
              {knowledgeBlock}
            </>
          ) : (
            <>
              {knowledgeBlock}
              {showContextSection && showPromptsSection && <Divider sx={{ my: 1.75, borderColor: SHELF_QUIET_BORDER }} />}
              {capabilityBlock}
            </>
          )}
        </Box>
      </Collapse>

      {showCollapseRail && (
        <Box
          sx={{
            width: collapseRailWidth,
            minWidth: collapseRailWidth,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            pt: hasExecuteChrome ? (isExpanded ? 0.25 : 1.125) : 0.5,
            bgcolor: 'background.paper',
          }}
        >
          <Tooltip
            title={
              typeof onCollapseRailClick === 'function' && isExpanded
                ? 'Close panel'
                : (isExpanded ? 'Collapse panel' : 'Expand panel')
            }
          >
            <IconButton
              size="small"
              onClick={() => {
                if (typeof onCollapseRailClick === 'function') {
                  onCollapseRailClick();
                  return;
                }
                setExpanded(!expanded);
              }}
              sx={{
                p: 0.35,
                width: 30,
                height: 30,
                color: colors.textSecondary,
                '&:hover': {
                  color: colors.textPrimary,
                  bgcolor: 'rgba(15, 23, 42, 0.035)',
                },
              }}
              aria-label={isExpanded ? 'Collapse panel' : 'Expand panel'}
            >
              {isExpanded ? <ChevronLeftIcon fontSize="small" /> : !hasContexts && !hasPrompts ? <KnowledgeIcon sx={{ fontSize: 17 }} /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {!isExpanded && (hasContexts || hasPrompts) && (
            <Typography
              variant="caption"
              sx={{
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
                mt: 2,
                fontSize: '0.7rem',
                color: 'text.secondary',
                whiteSpace: 'nowrap',
              }}
            >
              {[singleCapability && hasPrompts ? `${prompts.length} capability` : '', hasContexts ? `Knowledge · ${contexts.filter((context) => context.hasAccess !== false && includedContexts[context.id] !== false).length} included` : ''].filter(Boolean).join(' · ')}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );

  if (!mobileOverlay) return shelf;

  return (
    <Box
      role="presentation"
      onClick={onMobileOverlayClose}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: (theme) => theme.zIndex.tooltip,
        isolation: 'isolate',
        display: 'flex',
        alignItems: 'stretch',
        bgcolor: 'transparent',
      }}
    >
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{
          position: 'relative',
          zIndex: 1,
          height: '100%',
          width: '75vw',
          bgcolor: 'background.paper',
          opacity: 1,
          boxShadow: '12px 0 28px rgba(15, 23, 42, 0.18)',
        }}
      >
        {shelf}
      </Box>
    </Box>
  );
}
